import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Typography, Button, notification, Input, Form, Grid, theme, Modal, Select, Tag, Divider, Popconfirm } from "antd";
import { VideoCameraOutlined, PlusOutlined, DeleteOutlined, CloudServerOutlined, ShareAltOutlined, CloseOutlined, LinkOutlined, EditOutlined } from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useFriends } from "../hooks/useFriends";
import { ShareLinksModal } from "./ShareLinksModal";
import { listAlbums, addAlbum, updateAlbum, deleteAlbum, shareAlbum, listAlbumShares, revokeShare, DualcamApiError } from "./api";
import type { Album, AlbumShare } from "./types";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusTag } from "@dowty/ui";

const { useBreakpoint } = Grid;
const { Text } = Typography;

const albumDisplayName = (album: Album) => album.label || `${album.host.replace(/^https?:\/\//, "")}/${album.sharingId}`;

const DualcamAlbumsPage: React.FC = () => {
    const { user } = useAuth();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const t = (de: string, en: string) => isGerman ? de : en;
    const { token: designToken } = theme.useToken();
    const [api, contextHolder] = notification.useNotification();

    const [albums, setAlbums] = useState<Album[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [renamingId, setRenamingId] = useState<number | null>(null);
    const [renameDraft, setRenameDraft] = useState("");
    const renameActive = useRef(false);
    const [adding, setAdding] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [shareLinksOpen, setShareLinksOpen] = useState(false);
    const [form] = Form.useForm();

    const [shareAlbumTarget, setShareAlbumTarget] = useState<Album | null>(null);
    const [shares, setShares] = useState<AlbumShare[]>([]);
    const [sharesLoading, setSharesLoading] = useState(false);
    const [sharesError, setSharesError] = useState(false);
    const shareRequestId = useRef(0);
    const [selectedFriendId, setSelectedFriendId] = useState<number | null>(null);
    const [sharing, setSharing] = useState(false);
    const [revokingId, setRevokingId] = useState<number | null>(null);
    // Always starts empty, even for an album that already has a link attached: the backend never
    // returns the stored URL (it's a write capability, so it stays server-side), only whether one
    // exists. Replacing a link therefore means pasting the new one, not editing the old.
    const [uploadLinkDraft, setUploadLinkDraft] = useState("");
    const [savingUploadLink, setSavingUploadLink] = useState(false);
    // Lazy (autoLoad=false) — the friend list is only ever needed once the share modal is opened,
    // not on every visit to this page.
    const { friends, loading: friendsLoading, error: friendsError, reload: loadFriends } = useFriends(false);

    const load = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            setAlbums(await listAlbums(user.token));
            setLoadError(null);
        } catch (err) {
            setLoadError((err as Error).message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAdd = async (values: { url: string; password?: string; label?: string; uploadRequestUrl?: string }) => {
        if (!user) return;
        setAdding(true);
        try {
            const uploadLink = values.uploadRequestUrl?.trim() || null;
            await addAlbum(user.token, values.url.trim(), values.password || null, values.label?.trim() || null, uploadLink);
            form.resetFields();
            setAddOpen(false);
            await load();
            api.success({
                message: t("Album verknüpft", "Album linked"),
                description: uploadLink ? t("Uploads sind dafür aktiviert.", "Uploads are enabled for it.") : undefined,
                placement: "top",
            });
        } catch (err) {
            const message = err instanceof DualcamApiError ? err.message : t("Album konnte nicht verknüpft werden.", "Failed to link that album.");
            form.setFields([{ name: "url", errors: [message] }]);
        } finally {
            setAdding(false);
        }
    };

    const commitRename = async (album: Album, value: string) => {
        if (!user) return;
        const trimmed = value.trim();
        if (trimmed === (album.label ?? "")) return;
        setBusyId(album.id);
        try {
            // Pass the trimmed string as-is (even "") — updateAlbum only skips sending a field when
            // it's null/undefined, so this is how an empty value actually clears the label rather
            // than being silently dropped.
            await updateAlbum(user.token, album.id, { label: trimmed });
            setAlbums(prev => prev.map(a => (a.id === album.id ? { ...a, label: trimmed || null } : a)));
        } catch (err) {
            api.error({ message: "Failed to rename album", description: (err as Error).message, placement: "top" });
        } finally {
            setBusyId(null);
        }
    };

    const finishRename = (album: Album) => {
        if (!renameActive.current) return;
        renameActive.current = false;
        setRenamingId(null);
        void commitRename(album, renameDraft);
    };

    const handleDelete = async (album: Album) => {
        if (!user) return;
        setBusyId(album.id);
        try {
            await deleteAlbum(user.token, album.id);
            setAlbums(prev => prev.filter(a => a.id !== album.id));
        } catch (err) {
            api.error({ message: "Failed to remove album", description: (err as Error).message, placement: "top" });
        } finally {
            setBusyId(null);
        }
    };

    // Attaches (or, with an empty value, detaches) the album's Synology Photo Request link — what
    // makes an album accept uploads at all. Owner-only; the backend rejects it on a shared copy, so
    // the UI doesn't offer the field there either.
    const commitUploadLink = async (album: Album, value: string) => {
        if (!user || savingUploadLink) return;
        const trimmed = value.trim();
        setSavingUploadLink(true);
        try {
            const updated = await updateAlbum(user.token, album.id, { uploadRequestUrl: trimmed });
            setAlbums(prev => prev.map(a => (a.id === album.id ? { ...a, canUpload: updated.canUpload } : a)));
            setShareAlbumTarget(prev => (prev?.id === album.id ? { ...prev, canUpload: updated.canUpload } : prev));
            setUploadLinkDraft("");
            api.success({
                message: trimmed ? "Uploads enabled" : "Uploads disabled",
                description: trimmed
                    ? "Anyone you've shared this album with can now upload to it."
                    : "The Photo Request link was detached — the album is read-only again.",
                placement: "top",
            });
        } catch (err) {
            const message = err instanceof DualcamApiError ? err.message : (err as Error).message;
            api.error({ message: "Couldn't update uploads", description: message, placement: "top" });
        } finally {
            setSavingUploadLink(false);
        }
    };

    const openShareModal = async (album: Album, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user) return;
        const requestId = ++shareRequestId.current;
        setShareAlbumTarget(album);
        setShares([]);
        setSharesError(false);
        setUploadLinkDraft("");
        setSelectedFriendId(null);
        setSharesLoading(true);
        loadFriends();
        try {
            const loadedShares = await listAlbumShares(user.token, album.id);
            if (requestId !== shareRequestId.current) return;
            setShares(loadedShares);
        } catch {
            if (requestId === shareRequestId.current) setSharesError(true);
        } finally {
            if (requestId === shareRequestId.current) setSharesLoading(false);
        }
    };

    const retryShares = () => {
        if (!user || !shareAlbumTarget) return;
        const album = shareAlbumTarget;
        const requestId = ++shareRequestId.current;
        setSharesLoading(true);
        setSharesError(false);
        void listAlbumShares(user.token, album.id).then(loaded => {
            if (requestId === shareRequestId.current) setShares(loaded);
        }).catch(() => {
            if (requestId === shareRequestId.current) setSharesError(true);
        }).finally(() => {
            if (requestId === shareRequestId.current) setSharesLoading(false);
        });
    };

    const handleShare = async () => {
        if (!user || !shareAlbumTarget || selectedFriendId == null) return;
        const target = shareAlbumTarget;
        const requestId = shareRequestId.current;
        setSharing(true);
        try {
            await shareAlbum(user.token, target.id, selectedFriendId);
            const loadedShares = await listAlbumShares(user.token, target.id);
            if (requestId !== shareRequestId.current) return;
            setShares(loadedShares);
            setSelectedFriendId(null);
            api.success({ message: "Album shared", placement: "top" });
        } catch (err) {
            const message = err instanceof DualcamApiError ? err.message : "Failed to share that album.";
            api.error({ message: "Couldn't share album", description: message, placement: "top" });
        } finally {
            setSharing(false);
        }
    };

    const handleRevoke = async (share: AlbumShare) => {
        if (!user || !shareAlbumTarget) return;
        const target = shareAlbumTarget;
        const requestId = shareRequestId.current;
        setRevokingId(share.shareId);
        try {
            await revokeShare(user.token, target.id, share.shareId);
            if (requestId === shareRequestId.current) setShares(prev => prev.filter(s => s.shareId !== share.shareId));
        } catch (err) {
            api.error({ message: "Failed to unshare", description: (err as Error).message, placement: "top" });
        } finally {
            setRevokingId(null);
        }
    };

    const sharedFriendIds = new Set(shares.map(s => s.friendUserId));
    const friendOptions = friends
        .filter(f => !sharedFriendIds.has(f.userid))
        .map(f => ({ value: f.userid, label: f.username }));

    const renderLinkAlbumModal = () => (
        <Modal
            title={t("Album verknüpfen", "Link an album")}
            open={addOpen}
            afterOpenChange={open => {
                if (open) form.getFieldInstance("url")?.focus();
            }}
            onCancel={() => setAddOpen(false)}
            footer={null}
            destroyOnHidden
        >
            <Form form={form} layout="vertical" onFinish={handleAdd} requiredMark={false}>
                <Form.Item
                    name="url"
                    label={t("Freigabelink", "Share link")}
                    rules={[{ required: true, message: t("Füge eine Freigabe-URL oder Freigabe-ID ein.", "Paste a share URL or sharing ID") }]}
                    style={{ marginBottom: 12 }}
                >
                    <Input placeholder={t("Freigabe-URL oder nur das Ende, falls du das Album schon einmal verknüpft hast", "Share URL, or just the ending if you've linked one before")} />
                </Form.Item>
                <Form.Item name="password" label={t("Album-Passwort", "Album password")} style={{ marginBottom: 12 }}>
                    <Input.Password placeholder={t("Nur bei passwortgeschützten Alben", "Only if the album is protected")} />
                </Form.Item>
                <Form.Item name="label" label={t("Spitzname", "Nickname")} style={{ marginBottom: 12 }}>
                    <Input placeholder={t("Optional", "Optional")} />
                </Form.Item>
                {/* Optional, and offered here rather than only in the Share dialog: enabling uploads is
                    usually decided at the same moment as linking the album, and both links are created
                    side by side in DSM. Left empty the album is simply read-only, exactly as before. */}
                <Form.Item
                    name="uploadRequestUrl"
                    label={t("Photo-Request-Link", "Photo Request link")}
                    extra={t("Optional — damit kannst du und jede Person, mit der du das Album teilst, Dateien hochladen. In DSM: Synology Photos → Alben → Album auswählen → Photo-Request-Link erstellen.", "Optional — lets you and anyone you share with upload to this album. In DSM: Synology Photos → Albums → select the album → Create Photo Request link.")}
                    style={{ marginBottom: 16 }}
                >
                    <Input placeholder="https://your-nas/mo/request/…" />
                </Form.Item>
                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={adding} block>
                    {t("Album verknüpfen", "Link album")}
                </Button>
            </Form>
        </Modal>
    );

    const renderUploadSection = () => (
        shareAlbumTarget?.shared ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
            {shareAlbumTarget.canUpload
                ? "The owner has enabled uploads for this album, so you can add photos to it."
                : "The owner hasn't enabled uploads for this album."}
        </Text>
    ) : (
        <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Text strong style={{ fontSize: 13 }}>Uploads</Text>
                <StatusTag tone={shareAlbumTarget?.canUpload ? "success" : "info"}>
                    {shareAlbumTarget?.canUpload ? "Enabled" : "Off"}
                </StatusTag>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
                <Input
                    aria-label="Photo Request link"
                    style={{ flex: 1 }}
                    placeholder={shareAlbumTarget?.canUpload ? "Paste a new link to replace the current one" : "Paste the album's Photo Request link"}
                    value={uploadLinkDraft}
                    onChange={e => setUploadLinkDraft(e.target.value)}
                    onPressEnter={() => !savingUploadLink && shareAlbumTarget && uploadLinkDraft.trim() && commitUploadLink(shareAlbumTarget, uploadLinkDraft)}
                />
                <Button
                    type="primary"
                    loading={savingUploadLink}
                    disabled={!uploadLinkDraft.trim() || savingUploadLink}
                    onClick={() => shareAlbumTarget && commitUploadLink(shareAlbumTarget, uploadLinkDraft)}
                >
                    Save
                </Button>
                {shareAlbumTarget?.canUpload && (
                    <Button
                        danger
                        loading={savingUploadLink}
                        onClick={() => shareAlbumTarget && commitUploadLink(shareAlbumTarget, "")}
                    >
                        Turn off
                    </Button>
                )}
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                In DSM: Synology Photos → Albums → select this album → Create Photo Request
                link. Anyone you share the album with can then upload to it from here.
            </Text>
        </>
    )
    );

    const renderShareModal = () => (
        <Modal
            title={shareAlbumTarget ? `Share "${albumDisplayName(shareAlbumTarget)}"` : "Share album"}
            open={shareAlbumTarget !== null}
            onCancel={() => setShareAlbumTarget(null)}
            footer={null}
            destroyOnHidden
        >
            {friendsError && <ErrorState message={isGerman ? "Freunde konnten nicht geladen werden." : "Could not load friends."} retry={loadFriends} />}
            {friendsLoading && <LoadingState label={isGerman ? "Freunde werden geladen" : "Loading friends"} />}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <Select
                    className="sf-touch-target"
                    aria-label={isGerman ? "Freund nach Benutzernamen suchen" : "Search a friend by username"}
                    showSearch
                    style={{ flex: 1 }}
                    placeholder="Search a friend by username"
                    optionFilterProp="label"
                    options={friendOptions}
                    value={selectedFriendId}
                    onChange={setSelectedFriendId}
                    allowClear
                />
                <Button type="primary" loading={sharing} disabled={selectedFriendId == null || friendsLoading} onClick={handleShare}>
                    Share
                </Button>
            </div>

            {sharesLoading && <LoadingState label={isGerman ? "Freigaben werden geladen" : "Loading shares"} />}
            {sharesError && <ErrorState message={isGerman ? "Freigaben konnten nicht geladen werden." : "Could not load shares."} retry={retryShares} />}
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                {!sharesLoading && !sharesError && (shares.length === 0 ? "Not shared with anyone yet." : "Shared with")}
            </Text>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {!sharesLoading && !sharesError && shares.map(share => (
                    <Tag key={share.shareId}>
                        {share.friendUsername}
                        <Button type="text" size="small" loading={revokingId === share.shareId} disabled={revokingId !== null} aria-label={isGerman ? `${share.friendUsername} entfernen` : `Remove ${share.friendUsername}`} onClick={() => handleRevoke(share)} style={{ minWidth: 44, minHeight: 44 }} icon={<CloseOutlined />} />
                    </Tag>
                ))}
            </div>

            {/* Uploads live in this modal rather than their own screen because they're the same
                decision as sharing: who else gets to use this album. Synology Photos' public
                share links are view/download only, so uploading needs a separate "Photo Request"
                link the owner creates in DSM — dowtex holds it server-side and proxies uploads to
                it, which is why there's no NAS login to enter here. */}
            <Divider style={{ marginTop: 20, marginBottom: 12 }} />
            {renderUploadSection()}
        </Modal>
    );

    let albumListStatus: React.ReactNode = null;
    if (loading) albumListStatus = <LoadingState variant="list" label="Loading albums" />;
    else if (loadError) albumListStatus = <ErrorState message={loadError} retry={load} />;
    else if (albums.length === 0) albumListStatus = <EmptyState
        icon={<VideoCameraOutlined />}
        title={isGerman ? "Noch keine Alben verknüpft." : "No albums linked yet."}
        description={isGerman ? "Füge ein Album mit seinem Freigabelink hinzu." : "Add an album with its share link."}
    />;

    return (
        <div className="sf-content-column" style={{ padding: isMobile ? "16px" : "32px 24px" }}>
            {contextHolder}

            {/* Linking an album is a rare, one-off action — it lived in a permanently expanded card that
                pushed the actual album list below the fold on every visit. Behind a button now. */}
            <PageHeader title="DualCam" extra={
                <>
                {/* Sits next to Link album because this is the hub someone returns to when the thought is
                    "I need to stop sharing that" — by then the dialog the link came from is long gone. */}
                <Button aria-label={t("Freigabelinks", "Share links")} icon={<LinkOutlined />} onClick={() => setShareLinksOpen(true)}>
                    {isMobile ? null : t("Freigabelinks", "Share links")}
                </Button>
                <Button aria-label={t("Album-Freigabelink hinzufügen", "Add an album link")} type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
                    {isMobile ? null : t("Album verknüpfen", "Link album")}
                </Button>
                </>
            } />

            {renderLinkAlbumModal()}

                {albumListStatus ?? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {albums.map(album => (
                            <div
                                key={album.id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    padding: "10px 12px",
                                    borderRadius: designToken.borderRadius,
                                    transition: "background 0.15s",
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = designToken.colorFillQuaternary)}
                                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                            >
                                <Link to={`/dualcam/${album.id}`} style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0, color: "inherit", textDecoration: "none" }}>
                                    <CloudServerOutlined style={{ fontSize: 20, color: designToken.colorPrimary, flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <Text strong style={{ display: "block", lineHeight: 1.4, overflowWrap: "anywhere" }}>
                                            {albumDisplayName(album)}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {album.host.replace(/^https?:\/\//, "")}
                                            {album.shared && (album.sharedByUsername ? ` · Shared by ${album.sharedByUsername}` : " · Shared with you")}
                                            {album.canUpload && " · Uploads on"}
                                        </Text>
                                    </div>
                                </Link>
                                {renamingId === album.id ? (
                                    <Input
                                        aria-label="Album name"
                                        autoFocus
                                        size="small"
                                        value={renameDraft}
                                        onChange={e => setRenameDraft(e.target.value)}
                                        onPressEnter={() => finishRename(album)}
                                        onBlur={() => finishRename(album)}
                                        onKeyDown={e => {
                                            if (e.key === "Escape") {
                                                renameActive.current = false;
                                                setRenamingId(null);
                                            }
                                        }}
                                    />
                                ) : (
                                    <Button
                                        type="text"
                                        aria-label={`Rename ${albumDisplayName(album)}`}
                                        icon={<EditOutlined />}
                                        onClick={() => { renameActive.current = true; setRenameDraft(album.label ?? ""); setRenamingId(album.id); }}
                                        size="small"
                                    />
                                )}
                                <Button
                                    type="text"
                                    aria-label="Share album"
                                    icon={<ShareAltOutlined />}
                                    onClick={e => openShareModal(album, e)}
                                    size="small"
                                />
                                <Popconfirm title={isGerman ? "Album entfernen?" : "Remove album?"} okText={isGerman ? "Entfernen" : "Remove"} cancelText={isGerman ? "Abbrechen" : "Cancel"} okButtonProps={{ danger: true }} onConfirm={() => void handleDelete(album)}>
                                    <Button type="text" aria-label={isGerman ? "Album löschen" : "Delete album"} danger icon={<DeleteOutlined />} loading={busyId === album.id} onClick={e => e.stopPropagation()} size="small" />
                                </Popconfirm>
                            </div>
                        ))}
                    </div>
                )}

            <ShareLinksModal open={shareLinksOpen} onClose={() => setShareLinksOpen(false)} albums={albums} />

            {renderShareModal()}
        </div>
    );
};

export default DualcamAlbumsPage;
